import {
  addSeconds,
  differenceInSeconds,
  eachDayOfInterval,
  endOfDay,
  startOfDay,
} from 'date-fns';
import Konva from 'konva';
import _ from 'lodash';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Layer } from 'react-konva';
import {
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { TwTheme } from '../../../Tw';
import { getQualitativeColor } from '../../common/color.helper';
import { toDateFromLocaleStringDate } from '../../common/dateTimeHelper';
import DeadlineVisualization from '../../components/layout/DeadlineVisualization';
import ScheduledJobFeature, {
  ScheduledJobFeatureProps,
} from '../../layout/features/ScheduledJobFeature';
import TimelineFeature from '../../layout/features/TimelineFeature';
import TimelineRowFeature from '../../layout/features/TimelineRowFeature';
import { StageType } from '../../layout/stage/stage.helper';
import { stageStateById } from '../../layout/stage/stage.state';
import { viewerAppliedCachingSettings } from '../../layout/viewer/store/viewer.state';
import {
  analyzeGateUsageSchedule,
  analyzeGateUsageScheduleCustomThresholds,
  analyzeGateUsageScheduleWaitTimeCriticalThreshold,
  analyzeGateUsageScheduleWaitTimeLateThreshold,
  analyzeGateUsageScheduleWaitTimeWarningThreshold,
} from '../store/analyze.state';
import { scheduleHoveredJobId } from './schedule.state';
import { WaitTimeClassificationModel } from './waitTimeClassificationModel';

const colors = TwTheme.extend.colors;

export const jobFeatureHeight = 34;
export const ticksInPixel = 60 * 1000; // 1 min
export const stageHeight = 24 * 60;
export const jobTopMargin = 10;

const widthDay = 24 * 60; //24h*60min
const heightDay = jobFeatureHeight;

export type JobsTimelineLayerProps = {
  stageId: StageType;
  hiddenDocks?: Set<string>;
};

// Type for selected job info
type SelectedJobInfo = {
  jobId: string;
  deadline: Date;
  waitTime: number;
  resourceIndex: number;
  startTime: Date;
};

const SimulationJobsTimelineLayer: React.FC<JobsTimelineLayerProps> = props => {
  const scheduleLoadable = useRecoilValueLoadable(analyzeGateUsageSchedule);
  const setStageState = useSetRecoilState(stageStateById(props.stageId));
  const stageState = useRecoilValue(stageStateById(props.stageId));
  const cachingSettings = useRecoilValue(viewerAppliedCachingSettings);
  const waitTimeWarningThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeWarningThreshold,
  );
  const waitTimeLateThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeLateThreshold,
  );
  const waitTimeCriticalThreshold = useRecoilValue(
    analyzeGateUsageScheduleWaitTimeCriticalThreshold,
  );
  const customThresholds = useRecoilValue(
    analyzeGateUsageScheduleCustomThresholds,
  );
  const setHoveredJobId = useSetRecoilState(scheduleHoveredJobId);
  const ref = useRef<Konva.Layer>(null);

  // State for selected job to show deadline and wait time
  const [selectedJob, setSelectedJob] = useState<SelectedJobInfo | null>(null);

  // Handle escape key to clear selection
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedJob(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (scheduleLoadable.state === 'hasValue') {
      if (_.isEmpty(scheduleLoadable.contents)) return;
      const { startTime, completionTime } = scheduleLoadable.contents;
      const start = toDateFromLocaleStringDate(startTime);
      const end = toDateFromLocaleStringDate(completionTime);
      const xMin = startOfDay(start).getTime() / ticksInPixel;
      const xMax = endOfDay(end).getTime() / ticksInPixel;
      const days = 3; // 3 day
      const xScope = xMin + (days * 24 * 3600 * 1000) / ticksInPixel;

      const key = `${xMin}-${xMax}`;
      setStageState({
        autoSizeId: `auto-size-bay-${key}`,
        contentBounds: [
          [xMin, -stageHeight / 2],
          [Math.min(xMax, xScope), stageHeight / 2],
        ],
      });
    }
  }, [scheduleLoadable]);

  useEffect(() => {
    if (ref?.current?.isCached) {
      ref?.current?.clearCache();
    }
    if (
      cachingSettings.type === 'on' ||
      (cachingSettings.type === 'auto' && cachingSettings.ratio < 1)
    ) {
      console.debug(
        'Cache JobsTimelineLayer with ratio: ',
        cachingSettings.ratio,
      );
      ref?.current?.cache({
        pixelRatio: cachingSettings.ratio,
        hitCanvasPixelRatio: cachingSettings.ratio,
      });
    }
  });

  const hasInterval = scheduleLoadable.state === 'hasValue';
  const hasJobs = scheduleLoadable.state === 'hasValue';
  const hasAnalyzedJobs = scheduleLoadable.state === 'hasValue';
  const hasShifts = scheduleLoadable.state === 'hasValue';

  const isReady = hasInterval && hasJobs && hasAnalyzedJobs && hasShifts;
  if (!isReady) return null;

  // all loaded
  const schedule = scheduleLoadable.contents;

  const resources = schedule?.resources;
  if (_.isEmpty(resources)) return null;
  const { startTime, completionTime } = scheduleLoadable.contents;
  const start = toDateFromLocaleStringDate(startTime);
  const end = toDateFromLocaleStringDate(completionTime);

  // const jobs = picklistsAllLoadable.getValue();
  // const analyzedJobs = analyzedJobsLoadable.contents;
  // const shifts = shiftsLoadable.contents;
  // const analyzedJobsMap = _.keyBy(analyzedJobs, j => j.jobId);

  const resourceCount = _.size(resources);
  if (_.isEmpty(resources)) return null;

  return (
    <Layer
      onClick={evt => {
        // Clear selection when clicking on empty space
        if (evt.target === evt.target.getStage()) {
          setSelectedJob(null);
        }
      }}
    >
      {eachDayOfInterval({
        start,
        end,
      }).map((day, index) => {
        return (
          <TimelineFeature
            key={`day-${day}-${index}`}
            day={day}
            index={index}
            rowCount={resourceCount}
            maxShiftCount={1}
          />
        );
      })}

      {_(resources)
        // .sortBy(agentJobs => {
        //   const agentId = _.head(agentJobs)?.agentId;
        //   const firstShift = _(shifts)
        //     .filter(s => s.agentUser === agentId)
        //     .minBy(s => s.shiftStart);

        //   return firstShift.shiftStart?.getHours();
        // })
        .map((resource, resourceIndex) => {
          const resourceColor = getQualitativeColor(resource.id, 'policy');
          const isHidden = props.hiddenDocks?.has(resource.id);

          return (
            <Fragment key={`resource-${resource.id}`}>
              <TimelineRowFeature
                from={startOfDay(start)}
                to={endOfDay(end)}
                index={resourceIndex}
              />

              {_.map(resource.assignments, (appointment, index) => {
                const from = addSeconds(
                  toDateFromLocaleStringDate(appointment.startTime),
                  +appointment.dockingDuration,
                );
                const to = addSeconds(
                  toDateFromLocaleStringDate(appointment.completionTime),
                  -appointment.undockingDuration,
                );

                const deadline = toDateFromLocaleStringDate(
                  appointment.job.deadline,
                );
                const startTime = toDateFromLocaleStringDate(
                  appointment.startTime,
                );
                const waitTime = Math.max(
                  0,
                  differenceInSeconds(startTime, deadline),
                );

                // Use the new classification model
                const classificationModel = new WaitTimeClassificationModel(
                  waitTimeWarningThreshold,
                  waitTimeLateThreshold,
                  waitTimeCriticalThreshold,
                  customThresholds,
                );
                const classification = classificationModel.classify(waitTime);

                const jobFeatureProps: ScheduledJobFeatureProps = {
                  job: {
                    id: appointment.job.id,
                    from,
                    to,
                    resource: resource.id,
                    gapBefore: appointment.dockingDuration,
                    gapAfter: appointment.undockingDuration,
                  },
                  colors: classification.threshold.timelineColors,
                  // colors: resourceColor,
                  offsetY: resourceIndex * jobFeatureHeight,
                  onClick: (evt: Konva.KonvaEventObject<MouseEvent>) => {
                    console.log('Clicked job:', appointment.job.id);
                    // Toggle selection: if same job is clicked, unselect it
                    if (
                      selectedJob &&
                      selectedJob.jobId === appointment.job.id
                    ) {
                      setSelectedJob(null);
                    } else {
                      // Set selected job info for deadline and wait time display
                      setSelectedJob({
                        jobId: appointment.job.id,
                        deadline,
                        waitTime,
                        resourceIndex,
                        startTime,
                      });
                    }
                  },
                  onMouseEnter: () => setHoveredJobId(appointment.job.id),
                  onMouseLeave: () => setHoveredJobId(null),
                  opacity: isHidden
                    ? 0.5
                    : selectedJob && selectedJob.jobId !== appointment.job.id
                      ? 0.3
                      : 1,
                };
                return (
                  <ScheduledJobFeature
                    key={`job-${appointment.job.id}`}
                    {...jobFeatureProps}
                  />
                );
              })}
            </Fragment>
          );
        })
        .value()}

      {/* Render deadline and wait time visualization for selected job */}
      {selectedJob && (
        <DeadlineVisualization
          deadline={selectedJob.deadline}
          startTime={selectedJob.startTime}
          color={colors.benchmark.deadline}
          isSelected={true}
          isHovered={true}
          stageHeight={heightDay}
          ticksInPixel={ticksInPixel}
          showConnectionLine={true}
          connectionY={
            selectedJob.resourceIndex * jobFeatureHeight + jobFeatureHeight / 2
          }
          showFlag={true}
          showLabel={true}
          labelFormat="datetime"
        />
      )}
    </Layer>
  );
};

export default SimulationJobsTimelineLayer;
